import { debugLog } from '@ui-common/utils'
import {
  AnyActor,
  assertEvent,
  DoneActorEvent,
  ErrorActorEvent,
  fromPromise,
  sendTo,
  setup,
  SpecialTargets,
} from 'xstate'

import { promptMachine } from './promptMachine'
import { DataStepEvent, EditableMachineContext } from './types'

const INITIAL = 'initial'
const ABORT_PROMPT = 'abortPrompt'
const REEDIT_CANCELLED_PROMPT = 'reEditCancelledPrompt'
const INFO_PROMPT = 'infoPrompt'
export const SUBMITTING = 'submitting'
export const VALIDATING = 'validating'

export const SUBMIT_FAILED = 'submitFailed'

export interface FormStepActions<TStepOutputDataType> {
  proceed: (data: TStepOutputDataType) => void
  previous: () => void
  toSummary: (data: TStepOutputDataType) => void
  exit: () => void
}

/** State machine to handle form type step states
 *
 * @param outputActions callbacks to handle parent
 * @param parentMachine Deprecated: parentMachine to send events to
 */
export const formDataStepMachine = <TContext extends EditableMachineContext, TStepOutputDataType>(
  outputActions?: FormStepActions<TStepOutputDataType>,
  parentMachine?: AnyActor,
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
) => {
  const parent = parentMachine ?? SpecialTargets.Parent

  return setup({
    types: {} as {
      context: TContext
      events: DataStepEvent<TStepOutputDataType>
      input: TContext
    },
    actions: outputActions
      ? {
          exit: () => outputActions.exit(),
          logError: (_, error) => debugLog(error),
          proceedToNextStep: (_, params) => outputActions.proceed(params as TStepOutputDataType), // try to figure out the type
          goToPreviousStep: () => outputActions.previous(),
          goBackToSummary: (_, params) => outputActions.toSummary(params as TStepOutputDataType),
        }
      : {
          exit: sendTo(parent, { type: 'EXIT_WIZARD' }),
          logError: (_, error) => debugLog(error),
          proceedToNextStep: sendTo(parent, (_, data) => ({ type: 'NEXT_STEP', data })),
          goToPreviousStep: sendTo(parent, { type: 'PREVIOUS_STEP' }),
          goBackToSummary: sendTo(parent, (_, data) => ({
            type: 'GOTO_SUMMARY',
            data,
          })),
        },
    actors: {
      abortPromptMachine: promptMachine,
      reEditPromptMachine: promptMachine,
      infoPromptMachine: promptMachine,
      save: fromPromise(({ input }: { input: { data: TStepOutputDataType; context: TContext } }) => {
        return Promise.resolve(input.data)
      }),
    },
    guards: {
      isEditing: (_: { context: TContext }, isEditing: boolean): boolean => {
        debugLog(`Guard: isEditing: ${isEditing}`)
        return isEditing
      },
    },
  }).createMachine({
    context: ({ input }) => input,
    initial: INITIAL,
    states: {
      [INITIAL]: {
        on: {
          SUBMIT: SUBMITTING,
          VALIDATE: VALIDATING,
          BACK: [
            {
              guard: {
                type: 'isEditing',
                params: ({ context }) => context.isEditing,
              },
              actions: {
                type: 'goBackToSummary',
              },
            },
            { actions: [{ type: 'goToPreviousStep' }] },
          ],
          SHOW_ABORT_PROMPT: ABORT_PROMPT,
          SHOW_CANCEL_REEDIT_PROMPT: REEDIT_CANCELLED_PROMPT,
          SHOW_INFO_PROMPT: INFO_PROMPT,
        },
      },
      [VALIDATING]: {
        on: {
          SUBMIT: SUBMITTING,
          VALIDATION_FAILED: INITIAL,
          VALIDATION_SERVER_ERROR: SUBMIT_FAILED,
        },
      },
      [SUBMITTING]: {
        invoke: {
          id: 'save',
          src: 'save',
          input: ({ event, context }) => {
            assertEvent(event, 'SUBMIT')
            return { data: event.data, context }
          },
          onDone: [
            {
              guard: {
                type: 'isEditing',
                params: ({ context }) => context.isEditing,
              },
              actions: {
                type: 'goBackToSummary',
                params: ({ event }: { event: DoneActorEvent<TStepOutputDataType> }) => event.output,
              },
              target: INITIAL,
            },
            {
              actions: {
                type: 'proceedToNextStep',
                params: ({ event }: { event: DoneActorEvent<TStepOutputDataType> }) => event.output,
              },
              target: INITIAL,
            },
          ],
          onError: {
            target: SUBMIT_FAILED,
            actions: {
              type: 'logError',
              params: ({ event }: { event: ErrorActorEvent }) => event.error,
            },
          },
        },
      },
      [SUBMIT_FAILED]: {
        on: {
          SUBMIT: SUBMITTING,
          VALIDATE: VALIDATING,
        },
      },
      [ABORT_PROMPT]: {
        invoke: {
          id: 'abortPromptMachine',
          src: 'abortPromptMachine',
        },
        on: {
          PROMPT_CANCELLED: INITIAL,
          PROMPT_CONFIRMED: { actions: { type: 'exit' } },
        },
      },
      [REEDIT_CANCELLED_PROMPT]: {
        invoke: {
          id: 'reEditPromptMachine',
          src: 'reEditPromptMachine',
        },
        on: {
          PROMPT_CANCELLED: INITIAL,
          PROMPT_CONFIRMED: { actions: { type: 'goBackToSummary' } },
        },
      },
      [INFO_PROMPT]: {
        invoke: {
          id: 'infoPromptMachine',
          src: 'infoPromptMachine',
        },
        on: {
          PROMPT_CONFIRMED: INITIAL,
        },
      },
    },
  })
}
